import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, qs } from "@/lib/api";
import { BRANDS, brandById } from "@/lib/brands";
import { formatCents } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

/**
 * Billing dialogs for the contact action bar (crm-billing). Wire them to the pending buttons:
 *   "Take payment / new order" → <TakePaymentDialog contactId={c.id} onClose={...} />
 *   "Invoice / pay link"       → <InvoiceDialog contactId={c.id} onClose={...} />
 * Card numbers never touch the admin: "Open the secure card form" opens billing's card page (Stripe's embedded
 * form since 2026-10-27; Authorize.net's while billing.active_gateway says so).
 */

interface Price {
  id: number;
  name: string;
  type: "one_time" | "recurring" | "payment_plan";
  amount_cents: number;
  active: boolean;
  summary: string;
}

interface Product {
  id: number;
  brand_id: number;
  name: string;
  active: boolean;
  requires_shipping: boolean;
  prices: Price[];
  /** Event products (Virtual Boot Camp, Masterclass): the CRM event buyers are registered for, for 1 or 2 people. */
  settings_parsed?: { event?: { event_id: number; tickets?: number } | null } | null;
}

/** events_get, as the class-date select uses it. */
interface EventForDates {
  event: { id: number; name: string; time_zone: string };
  sessions: {
    id: number;
    name: string | null;
    starts_at: string;
    status: string;
    capacity: number | null;
    registered: number;
    attended: number;
    guests: number;
  }[];
}

export interface ClassDate {
  id: number;
  label: string;
  full: boolean;
}

/** Upcoming scheduled dates, as checkout lists them: a date is full when the seats taken (people and their guests)
 *  plus this purchase's tickets would pass its capacity. Full dates stay choosable; the server decides. */
export function classDates(e: EventForDates, tickets: number, now: Date = new Date()): ClassDate[] {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: e.event.time_zone || "America/Denver",
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
  return e.sessions
    .filter((x) => x.status === "scheduled" && new Date(x.starts_at).getTime() >= now.getTime())
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at) || a.id - b.id)
    .slice(0, 24)
    .map((x) => {
      const taken = x.registered + x.attended + x.guests;
      return { id: x.id, label: fmt.format(new Date(x.starts_at)) + (x.name ? `, ${x.name}` : ""), full: x.capacity != null && taken + tickets > x.capacity };
    });
}

interface Quote {
  total_cents: number;
  due_today_cents: number;
  discount_cents: number;
  promo_error?: string;
  schedule?: { seq: number; due_date: string; amount_cents: number }[];
}

interface InvoiceRow {
  id: number;
  number: string;
  amount_due_cents: number;
  amount_paid_cents: number;
  status: string;
  pay_token?: string | null;
}

interface OrderOut {
  id: number;
  total_cents: number;
  invoices: InvoiceRow[];
  next_step?: string;
}

interface Card {
  id: number;
  card_brand: string | null;
  last4: string | null;
  status: string;
  is_default: boolean;
}

interface ChargePreview {
  charge?: string;
  card?: string;
  invoice?: string;
  for?: string;
}

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function TakePaymentDialog({ contactId, onClose }: { contactId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [productId, setProductId] = useState<number | "">("");
  const [priceId, setPriceId] = useState<number | "">("");
  const [promo, setPromo] = useState("");
  const [ship, setShip] = useState({ name: "", line1: "", line2: "", city: "", region: "", postal_code: "", phone: "" });
  const [sessionId, setSessionId] = useState<number | "">("");
  const [guest, setGuest] = useState({ first_name: "", last_name: "", email: "" });
  const [order, setOrder] = useState<OrderOut | null>(null);
  const [chargePreview, setChargePreview] = useState<ChargePreview | null>(null);
  const [idemKey] = useState(() => `admin-${contactId}-${Date.now()}`);

  const products = useQuery({
    queryKey: ["billing-products"],
    queryFn: () => api<{ products: Product[] }>("/api/v1/products" + qs({ active_only: true, limit: 200 })).then((r) => r.products),
  });
  const cards = useQuery({
    queryKey: ["billing-cards", contactId],
    queryFn: () => api<{ cards: Card[] }>(`/api/v1/contacts/${contactId}/cards`).then((r) => r.cards.filter((c) => c.status === "active")),
  });
  const product = useMemo(() => products.data?.find((p) => p.id === productId), [products.data, productId]);
  const prices = product?.prices.filter((p) => p.active) ?? [];
  // Event products (audit C10): a class date, and on a 2-ticket product the guest, as checkout asks for them.
  const ev = product?.settings_parsed?.event?.event_id ? product.settings_parsed.event : null;
  const tickets = ev ? Math.max(1, ev.tickets ?? 1) : 0;
  const eventQ = useQuery({
    queryKey: ["event", ev?.event_id],
    enabled: !!ev,
    queryFn: () => api<EventForDates>(`/api/v1/events/${ev!.event_id}`),
  });
  const dates = useMemo(() => (eventQ.data ? classDates(eventQ.data, tickets) : []), [eventQ.data, tickets]);
  const chosenDate = sessionId === "" ? (dates.find((d) => !d.full) ?? dates[0]) : dates.find((d) => d.id === sessionId);
  const quote = useQuery({
    queryKey: ["billing-quote", priceId, promo, contactId],
    enabled: priceId !== "",
    queryFn: () => api<Quote>("/api/v1/products/quote", { json: { items: [{ price_id: priceId }], promo_code: promo || undefined, contact_id: contactId } }),
  });

  const create = useMutation({
    mutationFn: () =>
      api<OrderOut>("/api/v1/orders", {
        json: {
          contact_id: contactId,
          items: [{ price_id: priceId }],
          promo_code: promo || undefined,
          shipping_address: product?.requires_shipping ? { ...ship, country_code: "US" } : undefined,
          event_session_id: ev && chosenDate ? chosenDate.id : undefined,
          guest:
            tickets > 1 ? { first_name: guest.first_name.trim(), last_name: guest.last_name.trim(), email: guest.email.trim() } : undefined,
          idempotency_key: idemKey,
        },
      }),
    onSuccess: (o) => {
      setOrder(o);
      void qc.invalidateQueries({ queryKey: ["timeline", contactId] });
    },
    onError: (e) => toast(errText(e), "error"),
  });
  const invoice = order?.invoices.find((i) => i.status === "open");

  const openForm = useMutation({
    mutationFn: () => api<{ url: string }>(`/api/v1/invoices/${invoice!.id}/take-payment`, { method: "POST" }),
    onSuccess: (r) => {
      window.open(r.url, "_blank", "noopener");
      toast("The secure card form opened in a new tab");
    },
    onError: (e) => toast(errText(e), "error"),
  });
  const charge = useMutation({
    mutationFn: async (confirm: boolean) => {
      try {
        return await api<{ status: string; message: string }>(`/api/v1/invoices/${invoice!.id}/charge`, { json: { confirm } });
      } catch (e) {
        if (e instanceof ApiError && e.code === "confirm_required") {
          setChargePreview((e.details ?? {}) as ChargePreview);
          return null;
        }
        throw e;
      }
    },
    onSuccess: (r) => {
      if (!r) return;
      toast(r.message, r.status === "succeeded" ? undefined : "error");
      setChargePreview(null);
      void qc.invalidateQueries({ queryKey: ["billing-summary", contactId] });
      if (r.status === "succeeded") onClose();
    },
    onError: (e) => toast(errText(e), "error"),
  });
  const sendLink = useMutation({
    mutationFn: () => api(`/api/v1/invoices/${invoice!.id}/send`, { method: "POST" }),
    onSuccess: () => {
      toast("Pay link emailed");
      onClose();
    },
    onError: (e) => toast(errText(e), "error"),
  });

  const needsShip = !!product?.requires_shipping;
  const shipOK = !needsShip || (ship.name && ship.line1 && ship.city && ship.postal_code && ship.phone);
  const guestOK = tickets < 2 || (!!guest.first_name.trim() && !!guest.last_name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email.trim()));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(620px,calc(100vw-2rem))]">
        <DialogTitle>Take payment / new order</DialogTitle>
        <DialogDescription>Creates the order, then take the card on the secure form, charge a saved card, or email a pay link. The CRM never sees card numbers.</DialogDescription>
        {!order ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="tp-product">Product</Label>
              <Select
                id="tp-product"
                value={productId}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : "";
                  setProductId(id);
                  const p = products.data?.find((x) => x.id === id);
                  setPriceId(p?.prices.find((x) => x.active)?.id ?? "");
                  setSessionId("");
                  setGuest({ first_name: "", last_name: "", email: "" });
                }}
              >
                <option value="">{products.isLoading ? "Loading…" : "Choose a product"}</option>
                {(products.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {brandById(p.brand_id)?.short} · {p.name}
                  </option>
                ))}
              </Select>
            </div>
            {prices.length > 1 && (
              <div className="space-y-1">
                <Label htmlFor="tp-price">Price</Label>
                <Select id="tp-price" value={priceId} onChange={(e) => setPriceId(e.target.value ? Number(e.target.value) : "")}>
                  {prices.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}: {p.summary}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {ev && (
              <div className="space-y-1">
                <Label htmlFor="tp-date">Class date{eventQ.data ? ` (${eventQ.data.event.name})` : ""}</Label>
                {eventQ.isLoading ? (
                  <p className="text-xs text-[var(--color-muted-foreground)]">Loading class dates…</p>
                ) : dates.length === 0 ? (
                  <p className="text-xs text-[var(--color-warning-fg)]">
                    No upcoming class dates. The buyer is registered for the next open date once one is added and the order is paid.
                  </p>
                ) : (
                  <>
                    <Select id="tp-date" value={chosenDate?.id ?? ""} onChange={(e) => setSessionId(e.target.value ? Number(e.target.value) : "")}>
                      {dates.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.label}
                          {d.full ? " (full)" : ""}
                        </option>
                      ))}
                    </Select>
                    {chosenDate?.full && (
                      <p className="text-xs text-[var(--color-warning-fg)]">This date shows as full. The CRM decides when the order is paid; staff can move them on the roster.</p>
                    )}
                  </>
                )}
              </div>
            )}
            {tickets > 1 && (
              <div className="grid grid-cols-2 gap-2">
                <p className="col-span-2 text-xs font-medium text-[var(--color-muted-foreground)]">
                  For 2 people: the guest (they get the same access and their own registration)
                </p>
                <div className="space-y-1">
                  <Label htmlFor="tp-gfirst">Guest first name</Label>
                  <Input id="tp-gfirst" value={guest.first_name} onChange={(e) => setGuest({ ...guest, first_name: e.target.value })} autoComplete="off" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="tp-glast">Guest last name</Label>
                  <Input id="tp-glast" value={guest.last_name} onChange={(e) => setGuest({ ...guest, last_name: e.target.value })} autoComplete="off" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="tp-gemail">Guest email (their own, not the buyer's)</Label>
                  <Input id="tp-gemail" type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} autoComplete="off" />
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="tp-promo">Promo code (optional)</Label>
              <Input id="tp-promo" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} />
            </div>
            {needsShip && (
              <div className="grid grid-cols-2 gap-2">
                <p className="col-span-2 text-xs font-medium text-[var(--color-muted-foreground)]">Ships by mail: shipping address</p>
                <Input className="col-span-2" placeholder="Name" value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })} />
                <Input className="col-span-2" placeholder="Street address" value={ship.line1} onChange={(e) => setShip({ ...ship, line1: e.target.value })} />
                <Input className="col-span-2" placeholder="Apartment, suite (optional)" value={ship.line2} onChange={(e) => setShip({ ...ship, line2: e.target.value })} />
                <Input placeholder="City" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} />
                <Input placeholder="State" value={ship.region} onChange={(e) => setShip({ ...ship, region: e.target.value })} />
                <Input placeholder="ZIP" value={ship.postal_code} onChange={(e) => setShip({ ...ship, postal_code: e.target.value })} />
                <Input placeholder="Phone (Lulu needs one)" value={ship.phone} onChange={(e) => setShip({ ...ship, phone: e.target.value })} />
              </div>
            )}
            {quote.data && (
              <div className="rounded-md border p-3 text-sm">
                {quote.data.promo_error && <p className="mb-1 text-[var(--color-danger-fg)]">{quote.data.promo_error}</p>}
                {quote.data.discount_cents > 0 && <p>Discount: {formatCents(quote.data.discount_cents)}</p>}
                <p>
                  Total <span className="num font-medium">{formatCents(quote.data.total_cents)}</span>
                  {quote.data.due_today_cents !== quote.data.total_cents && (
                    <>
                      {" "}
                      · due today <span className="num font-medium">{formatCents(quote.data.due_today_cents)}</span>
                    </>
                  )}
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={priceId === "" || !shipOK || !guestOK || create.isPending || !!quote.data?.promo_error} onClick={() => create.mutate()}>
                {create.isPending ? "Creating…" : "Create order"}
              </Button>
            </div>
          </div>
        ) : !invoice ? (
          <div className="space-y-3 text-sm">
            <p>Order {order.id} is complete; nothing is due today.</p>
            <div className="flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : chargePreview ? (
          <div className="space-y-3 text-sm">
            <p className="font-medium">
              Charge {chargePreview.charge} to {chargePreview.card}?
            </p>
            <p className="text-[var(--color-muted-foreground)]">
              Invoice {chargePreview.invoice}: {chargePreview.for}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setChargePreview(null)}>
                Back
              </Button>
              <Button onClick={() => charge.mutate(true)} disabled={charge.isPending}>
                {charge.isPending ? "Charging…" : "Charge the card"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p>
              Order {order.id}: invoice <span className="num">{invoice.number}</span> for{" "}
              <span className="num font-medium">{formatCents(invoice.amount_due_cents - invoice.amount_paid_cents)}</span> is ready.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => openForm.mutate()} disabled={openForm.isPending}>
                Open the secure card form
              </Button>
              {(cards.data?.length ?? 0) > 0 && (
                <Button variant="outline" onClick={() => charge.mutate(false)} disabled={charge.isPending}>
                  Charge a saved card
                </Button>
              )}
              <Button variant="outline" onClick={() => sendLink.mutate()} disabled={sendLink.isPending}>
                Email a pay link
              </Button>
            </div>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              The card form opens in a new tab; type the card there while the customer reads it to you. If their bank asks them to confirm the payment, you can't
              do that for them: email the pay link instead.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface Line {
  description: string;
  amount: string;
}

export function InvoiceDialog({ contactId, defaultBrandId, onClose }: { contactId: number; defaultBrandId?: number; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [brand, setBrand] = useState<number>(defaultBrandId ?? 2);
  const [lines, setLines] = useState<Line[]>([{ description: "", amount: "" }]);
  const [due, setDue] = useState("");
  const [send, setSend] = useState(false);
  const [result, setResult] = useState<{ number: string; pay_url?: string } | null>(null);
  const cents = (s: string) => Math.round(Number(s.replace(/[$,]/g, "")) * 100);
  const valid = lines.every((l) => l.description.trim() && cents(l.amount) > 0);
  const create = useMutation({
    mutationFn: () =>
      api<{ number: string; pay_url?: string }>("/api/v1/invoices", {
        json: {
          contact_id: contactId,
          brand_id: brand,
          lines: lines.map((l) => ({ description: l.description.trim(), amount_cents: cents(l.amount) })),
          due_date: due || undefined,
          send,
        },
      }),
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ["timeline", contactId] });
      toast(send ? "Invoice created and emailed" : "Invoice created");
    },
    onError: (e) => toast(errText(e), "error"),
  });
  const total = lines.reduce((s, l) => s + (cents(l.amount) || 0), 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Invoice / pay link</DialogTitle>
        <DialogDescription>Creates an invoice with a secure pay link the customer can pay by card or a wallet (Apple Pay, Google Pay, Link…).</DialogDescription>
        {result ? (
          <div className="space-y-3 text-sm">
            <p>
              Invoice <span className="num">{result.number}</span> is ready.
            </p>
            {result.pay_url && (
              <div className="flex gap-2">
                <Input readOnly value={result.pay_url} aria-label="Pay link" />
                <Button variant="outline" onClick={() => void navigator.clipboard.writeText(result.pay_url!).then(() => toast("Link copied"))}>
                  Copy
                </Button>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="inv-brand">Brand</Label>
              <Select id="inv-brand" value={brand} onChange={(e) => setBrand(Number(e.target.value))}>
                {BRANDS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_8rem] gap-2">
                <Input
                  aria-label={`Line ${i + 1}: what it's for`}
                  placeholder="What it's for"
                  value={l.description}
                  onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                />
                <Input aria-label={`Line ${i + 1}: amount in dollars`} placeholder="Amount ($)" inputMode="decimal" value={l.amount} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
              </div>
            ))}
            <Button variant="ghost" size="sm" onClick={() => setLines([...lines, { description: "", amount: "" }])}>
              Add a line
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="inv-due">Due date (optional)</Label>
                <Input id="inv-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
              </div>
              <label className="mt-5 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} />
                Email it to the customer now
              </label>
            </div>
            <div className="flex items-center justify-end gap-2">
              <span className="num mr-auto text-sm">Total {formatCents(total)}</span>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
                {create.isPending ? "Creating…" : "Create invoice"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
